import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { getTranslatedPath } from '../util/urlHelpers';
import { SUPPORTED_LANGUAGES } from '../util/constants';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const DeleteAccountPage = loadable(() => import(/* webpackChunkName: "DeleteAccountPage" */ '../containers/DeleteAccountPage/DeleteAccountPage'));
const NotificationsPage = loadable(() => import(/* webpackChunkName: "NotificationsPage" */ '../containers/NotificationsPage/NotificationsPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxBasePage = loadable(() => import(/* webpackChunkName: "InboxBasePage" */ '../containers/InboxPage/InboxBasePage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const AdminWalletPage = loadable(() => import(/* webpackChunkName: "AdminWalletPage" */ '../containers/AdminWalletPage/AdminWalletPage'));
const ConsolePage = loadable(() => import(/* webpackChunkName: "ConsolePage" */ '../containers/ConsolePage/ConsolePage'));
const ConsoleActivityLogPage = loadable(() => import(/* webpackChunkName: "ConsoleActivityLogPage" */ '../containers/ConsoleActivityLogPage/ConsoleActivityLogPage'));
const ConsoleUsersPage = loadable(() => import(/* webpackChunkName: "ConsoleUsersPage" */ '../containers/ConsoleUsersPage/ConsoleUsersPage'));
const ConsoleListingsPage = loadable(() => import(/* webpackChunkName: "ConsoleListingsPage" */ '../containers/ConsoleListingsPage/ConsoleListingsPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const CartPage = loadable(() => import(/* webpackChunkName: "CartPage" */ '../containers/CartPage/CartPage'));
const StoreProfilePage = loadable(() => import(/* webpackChunkName: "StoreProfilePage" */ '../containers/StoreProfilePage/StoreProfilePage'));
const StoreListingsPage = loadable(() => import(/* webpackChunkName: "StoreListingsPage" */ '../containers/StoreListingsPage/StoreListingsPage'));
const StoreCategoriesPage = loadable(() => import(/* webpackChunkName: "StoreCategoriesPage" */ '../containers/StoreCategoriesPage/StoreCategoriesPage'));
const StoreBasicInformationPage = loadable(() => import(/* webpackChunkName: "StoreBasicInformationPage" */ '../containers/StoreBasicInformationPage/StoreBasicInformationPage'));
const StoreFaqsPage = loadable(() => import(/* webpackChunkName: "StoreFaqsPage" */ '../containers/StoreFaqsPage/StoreFaqsPage'));
const StoreHoursPage = loadable(() => import(/* webpackChunkName: "StoreHoursPage" */ '../containers/StoreHoursPage/StoreHoursPage'));
const StoreBlogPage = loadable(() => import(/* webpackChunkName: "StoreBlogPage" */ '../containers/StoreBlogPage/StoreBlogPage'));
const StoreBlogPreviewPage = loadable(() => import(/* webpackChunkName: "StoreBlogPreviewPage" */ '../containers/StoreBlogPage/StoreBlogPreviewPage/StoreBlogPreviewPage'));
const MediaLibraryPage = loadable(() => import(/* webpackChunkName: "MediaLibraryPage" */ '../containers/MediaLibraryPage/MediaLibraryPage'));
const TrustAndVerificationPage = loadable(() => import(/* webpackChunkName: "TrustAndVerificationPage" */ '../containers/TrustAndVerificationPage/TrustAndVerificationPage'));
const DefaultSettingsPage = loadable(() => import(/* webpackChunkName: "DefaultSettingsPage" */ '../containers/DefaultSettingsPage/DefaultSettingsPage'));
const StoreQRCodePage = loadable(() => import(/* webpackChunkName: "StoreQRCodePage" */ '../containers/StoreQRCodePage/StoreQRCodePage'));
// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

const AlgoliaSearchPage = loadable(() => import(/* webpackChunkName: "AlgoliaSearchPage" */ '../containers/SearchPage/AlgoliaSearchPage/AlgoliaSearchPage'));
const ListingWizardPage = loadable(() => import(/* webpackChunkName: "ListingWizardPage" */ '../containers/ListingWizard/ListingWizardPage'));

const PublishedListingPage = loadable(() => import(/* webpackChunkName: "PublishedListingPage" */ '../containers/PublishedListingPage/PublishedListingPage'));
const AIUpdateListingPage = loadable(() => import(/* webpackChunkName: "AIUpdateListingPage" */ '../containers/AIUpdateListingPage/AIUpdateListingPage'));

const BlogPreviewPage = loadable(() => import(/* webpackChunkName: "BlogsPreviewPage" */ '../containers/BlogsPreviewPage/BlogsPreviewPage'));
const BlogContentPage = loadable(() => import(/* webpackChunkName: "BlogContentPage" */ '../containers/BlogContentPage/BlogContentPage'));
const StoreBlogContentPage = loadable(() => import(/* webpackChunkName: "StoreBlogContentPage" */ '../containers/StoreBlogContentPage/StoreBlogContentPage'));

const CoachRentalPage = loadable(() => import(/* webpackChunkName: "CoachRentalPage" */ '../containers/CoachRentalPage/coachRentalPage'));

const StartRentalBusinessStemToysPage = loadable(() => import(/* webpackChunkName: "StartRentalBusinessStemToysPage" */ '../containers/StartRentalBusinessStemToysPage/StartRentalBusinessStemToysPage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'NotificationsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'DeleteAccountPage'
];


export const STORE_SETTINGS_PAGES = [
  'TrustAndVerificationPage',
  'StoreBasicInformationPage',
  'StoreFaqsPage',
  'StoreHoursPage',
  'StoreBlogPage',
  'MediaLibraryPage',
  'StoreCategoriesPage',
  'DefaultSettingsPage',
  'StoreQRCodePage'
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.


const routeConfiguration = (layoutConfig = {}) => {
  const SearchPage = AlgoliaSearchPage;
  const ListingPage = ListingPageCarousel;

  const routes = [
    {
      fallbackPath: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      fallbackPath: '/coach-rental',
      name: 'CoachRentalPage',
      component: CoachRentalPage,
    },
    {
      fallbackPath: '/start-a-rental-business/stem-toys',
      name: 'StartRentalBusinessStemToysPage',
      component: StartRentalBusinessStemToysPage,
    },
    {
      fallbackPath: '/cart',
      name: 'CartPage',
      component: CartPage,
      loadData: pageDataLoadingAPI.CartPage.loadData,
    },
    {
      fallbackPath: '/page/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/account/delete-profile',
      name: 'DeleteAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: DeleteAccountPage,
    },
    {
      fallbackPath: '/search',
      name: 'SearchPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.AlgoliaSearchPage.loadData,
    },
    {
      fallbackPath: '/listing',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      fallbackPath: '/rentals/:city/:category/:slug/:id',
      name: 'ListingPageCityCategory',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      // NOTE: /rentals/:city/:slug/:id and /rentals/:category/:slug/:id have identical
      // three-segment structures — the router cannot distinguish them by pattern alone.
      // ListingPageCity is matched first; the component handles mismatch via redirect.
      fallbackPath: '/rentals/:city/:slug/:id',
      name: 'ListingPageCity',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      fallbackPath: '/rentals/:category/:slug/:id',
      name: 'ListingPageCategory',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      fallbackPath: '/rentals/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      fallbackPath: '/l/:slug/:id/checkout',
      name: 'CheckoutPageOld',
      component: ({ params }) => <NamedRedirect name="CheckoutPage" params={{ ...params }} />,
    },
    {
      fallbackPath: '/listing/:slug/:id/checkout',
      name: 'CheckoutPage',
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
      loadData: pageDataLoadingAPI.CheckoutPage.loadData,
    },
    {
      fallbackPath: '/l/:slug/:id/:variant',
      name: 'ListingPageVariantOld',
      component: ({ params }) => <NamedRedirect name="ListingPageVariant" params={{ ...params }} />,
    },
    {
      fallbackPath: '/listing/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      fallbackPath: '/listing/new',
      name: 'NewListingPage',
      component: () => (
        <NamedRedirect
          name="CreateAIListing"
          params={{ action: 'create', step: 'input' }}
        />
      ),
    },
    {
      fallbackPath: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    // ── Store blog content pages ───────────────────────────────────────────────
    // MUST appear before generic ProfilePage routes (/rental-owner/:city/:slug/:id)
    // to prevent /rental-owner/:userId/blog/:postSlug from being swallowed by
    // the 4-segment ProfilePage pattern (which treats "blog" as :slug and the
    // post slug as :id, causing a Sharetribe users/show 400 error).
    {
      fallbackPath: '/rental-owner/:userId/blog/:slug',
      name: 'StoreBlogContentPage',
      component: StoreBlogContentPage,
      loadData: pageDataLoadingAPI.StoreBlogContentPage.loadData,
    },
    {
      fallbackPath: '/store/:userId/blog/:slug',
      name: 'StoreBlogContentPageLegacyStore',
      component: StoreBlogContentPage,
      loadData: pageDataLoadingAPI.StoreBlogContentPage.loadData,
    },
    {
      fallbackPath: '/store-blog/:blogId/:userId',
      name: 'StoreBlogContentPageLegacy',
      component: StoreBlogContentPage,
      loadData: pageDataLoadingAPI.StoreBlogContentPage.loadData,
    },
    // ── City routes (three-segment: city + slug + UUID) ───────────────────────
    // MUST appear before no-city routes to prevent greedy matching
    {
      fallbackPath: '/rental-owner/:citySlug/:storeSlug/:userId/listings',
      name: 'StoreListingsPageCity',
      component: StoreListingsPage,
      loadData: pageDataLoadingAPI.StoreListingsPage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:storeSlug/:userId/listings',
      name: 'StoreListingsPage',
      component: StoreListingsPage,
      loadData: pageDataLoadingAPI.StoreListingsPage.loadData,
    },
    // ── No-city tab routes (slug + UUID + tab) ──────────────────────────────────
    // MUST appear before ProfilePage (/rental-owner/:city/:slug/:id): both have four
    // segments, and matchPathname takes the first match, so /rental-owner/:slug/:id/reviews
    // used to load ProfilePage with "reviews" as the user ID and render "Error in loading
    // the profile". A fixed last segment cannot be a UUID, so no city URL matches these.
    {
      fallbackPath: '/rental-owner/:slug/:id/about',
      name: 'ProfilePageNoCityAbout',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:slug/:id/reviews',
      name: 'ProfilePageNoCityReviews',
      component: StoreProfilePage,
      loadData: (params, search, config) =>
        pageDataLoadingAPI.StoreProfilePage.loadData(
          { ...params, tab: 'reviews' },
          search,
          config
        ),
    },
    {
      fallbackPath: '/rental-owner/:slug/:id/gallery',
      name: 'ProfilePageNoCityGallery',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:slug/:id/faqs',
      name: 'ProfilePageNoCityFaqs',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:slug/:id/blog',
      name: 'ProfilePageNoCityBlog',
      component: StoreProfilePage,
      loadData: (params, search, config) =>
        pageDataLoadingAPI.StoreProfilePage.loadData(
          { ...params, tab: 'blog' },
          search,
          config
        ),
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id',
      name: 'ProfilePage',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id/about',
      name: 'ProfilePageAbout',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id/reviews',
      name: 'ProfilePageReviews',
      component: StoreProfilePage,
      loadData: (params, search, config) =>
        pageDataLoadingAPI.StoreProfilePage.loadData(
          { ...params, tab: 'reviews' },
          search,
          config
        ),
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id/gallery',
      name: 'ProfilePageGallery',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id/faqs',
      name: 'ProfilePageFaqs',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/rental-owner/:city/:slug/:id/blog',
      name: 'ProfilePageBlog',
      component: StoreProfilePage,
      loadData: (params, search, config) =>
        pageDataLoadingAPI.StoreProfilePage.loadData(
          { ...params, tab: 'blog' },
          search,
          config
        ),
    },
    // ── No-city fallback routes (two-segment: slug + UUID) ────────────────────
    // Used when store publicData has no city value
    {
      fallbackPath: '/rental-owner/:slug/:id',
      name: 'ProfilePageNoCity',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    // ── Legacy route (UUID-only) ───────────────────────────────────────────────
    // Retained for server-side 301 redirect handling — do not remove
    {
      fallbackPath: '/rental-owner/:id',
      name: 'ProfilePageLegacy',
      component: StoreProfilePage,
      loadData: pageDataLoadingAPI.StoreProfilePage.loadData,
    },
    {
      fallbackPath: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    {
      fallbackPath: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      fallbackPath: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      fallbackPath: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      fallbackPath: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      fallbackPath: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxBasePage,
      loadData: pageDataLoadingAPI.InboxBasePage.loadData,
    },
    {
      fallbackPath: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      fallbackPath: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      fallbackPath: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      fallbackPath: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      fallbackPath: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      fallbackPath: '/listing',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      fallbackPath: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      fallbackPath: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      fallbackPath: '/account/notifications',
      name: 'NotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: NotificationsPage,
      loadData: pageDataLoadingAPI.NotificationsPage.loadData,
    },
    {
      fallbackPath: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      fallbackPath: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      fallbackPath: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      fallbackPath: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    // Store Settings Routes
    {
      fallbackPath: '/store-settings',
      name: 'StoreSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="StoreBasicInformationPage" />,
    },
    {
      fallbackPath: '/store-settings/basic-information',
      name: 'StoreBasicInformationPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreBasicInformationPage,
      loadData: pageDataLoadingAPI.StoreBasicInformationPage.loadData,
    },
    {
      fallbackPath: '/store-settings/faqs',
      name: 'StoreFaqsPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreFaqsPage,
      loadData: pageDataLoadingAPI.StoreFaqsPage.loadData,
    },
    {
      fallbackPath: '/store-settings/hours',
      name: 'StoreHoursPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreHoursPage,
      loadData: pageDataLoadingAPI.StoreHoursPage.loadData,
    },
    {
      fallbackPath: '/store-settings/blog',
      name: 'StoreBlogPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreBlogPage,
      loadData: pageDataLoadingAPI.StoreBlogPage?.loadData,
    },
    {
      fallbackPath: '/store-settings/categories',
      name: 'StoreCategoriesPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreCategoriesPage,
      loadData: pageDataLoadingAPI.StoreCategoriesPage?.loadData,
    },
    {
      fallbackPath: '/store-settings/media-library',
      name: 'MediaLibraryPage',
      auth: true,
      authPage: 'LoginPage',
      component: MediaLibraryPage,
      loadData: pageDataLoadingAPI.MediaLibraryPage?.loadData,
    },
    {
      fallbackPath: '/store-settings/trust-and-verification',
      name: 'TrustVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: TrustAndVerificationPage,
      loadData: pageDataLoadingAPI.TrustAndVerificationPage.loadData,
    },
    {
      fallbackPath: '/store-settings/defaults',
      name: 'DefaultSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: DefaultSettingsPage,
      loadData: pageDataLoadingAPI.DefaultSettingsPage.loadData,
    },
    {
      fallbackPath: '/store-settings/qr-code',
      name: 'StoreQRCodePage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreQRCodePage,
      loadData: pageDataLoadingAPI.StoreQRCodePage.loadData,
    },
    {
      fallbackPath: '/admin/:tab',
      name: 'AdminWalletPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminWalletPage,
      loadData: pageDataLoadingAPI.AdminWalletPage.loadData,
    },
    {
      fallbackPath: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      fallbackPath: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      fallbackPath: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      fallbackPath: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      fallbackPath: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      fallbackPath: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      fallbackPath: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      fallbackPath: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },
    {
      fallbackPath: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
    },
    {
      fallbackPath: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    {
      fallbackPath: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage,
    },
    {
      fallbackPath: '/rentanything-wizard/new',
      name: 'CreateAIListing',
      component: (props) => <NamedRedirect name="RentAnythingWizardPage" params={{ action: 'create', step: 'input' }} />,
    },
    {
      fallbackPath: '/rentanything-wizard/listing/:action/:step/:listingToken?',
      name: 'RentAnythingWizardPage',
      component: ListingWizardPage,
      loadData: pageDataLoadingAPI.ListingWizardPage.loadData,
    },
    {
      fallbackPath: '/published-listing/:slug/:listingId',
      name: 'PublishedListingPage',
      component: PublishedListingPage,
      loadData: pageDataLoadingAPI.PublishedListingPage.loadData,
      auth: true,
      authPage: 'LoginPage',
    },
    {
      fallbackPath: '/blog-preview/:listingId',
      name: 'BlogPreviewPage',
      component: BlogPreviewPage,
      setInitialValues: pageDataLoadingAPI.BlogPreviewPage.setInitialValues,
      auth: true,
      authPage: 'LoginPage',
    },
    {
      fallbackPath: '/store/blog/preview/:userId',
      name: 'StoreBlogPreviewPage',
      component: StoreBlogPreviewPage,
      setInitialValues: pageDataLoadingAPI.StoreBlogPreviewPage.setInitialValues,
      auth: true,
      authPage: 'LoginPage',
    },
    {
      fallbackPath: '/ai/update-listing/:listingId',
      name: 'AIUpdateListingPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: (props) => <NamedRedirect name="AIUpdateListingPage" params={{ listingId: props?.params.listingId, tab: "details" }} />,
    },
    {
      fallbackPath: '/ai/update-listing/:listingId/:tab',
      name: 'AIUpdateListingPage',
      component: AIUpdateListingPage,
      loadData: pageDataLoadingAPI.AIUpdateListingPage.loadData,
      auth: true,
      authPage: 'LoginPage',
    },
    {
      fallbackPath: '/blog-content/:blogId/:listingId',
      name: 'BlogContentPage',
      component: BlogContentPage,
      loadData: pageDataLoadingAPI.BlogContentPage.loadData,
    },
    // Admin console (P3-2). Added last so no existing route's position changes, which matters
    // because several route families here are told apart only by the order they are declared
    // in. It sits at /console rather than under /admin because '/admin/:tab' is a catch-all
    // for any single segment and would swallow a console path.
    //
    // No `auth` flag: the server decides who may see this page before anything renders
    // (server/console/gate.js). Navigating to it inside the app never reaches the server at
    // all, so the gate is not what stops that; what stops it is that the page draws nothing
    // until the sections endpoint replies, and that endpoint is gated the same way. A
    // client-side flag here would be a second, weaker answer to the same question.
    {
      fallbackPath: '/console',
      name: 'ConsolePage',
      component: ConsolePage,
    },
    // The console's activity log (P3-3). Routes here are exact by default
    // (src/routing/Routes.js:261), so this is a separate route rather than something the
    // console route above would have matched. Gated on the server the same way: the page
    // gate refuses the path unless the console switch, the admin check and this section's
    // own switch all pass, and the endpoint it reads is gated identically.
    {
      fallbackPath: '/console/activity-log',
      name: 'ConsoleActivityLogPage',
      component: ConsoleActivityLogPage,
    },
    // The console's members section (P3-4). Two routes, one component: the search page and
    // one member's page are the same screen in two states, and sharing the component keeps
    // "open a member, then go back" from reloading the whole section. Gated on the server the
    // same way as every console path, and both endpoints behind this section's own switch.
    {
      fallbackPath: '/console/users',
      name: 'ConsoleUsersPage',
      component: ConsoleUsersPage,
    },
    {
      fallbackPath: '/console/users/:id',
      name: 'ConsoleUserPage',
      component: ConsoleUsersPage,
    },
    // The console's listings section (P3-5). Two routes, one component: the search page and
    // one listing's page are the same screen in two states. Gated on the server the same way
    // as every console path, and both endpoints behind this section's own switch.
    {
      fallbackPath: '/console/listings',
      name: 'ConsoleListingsPage',
      component: ConsoleListingsPage,
    },
    {
      fallbackPath: '/console/listings/:id',
      name: 'ConsoleListingPage',
      component: ConsoleListingsPage,
    },
  ];

  const localizedRoutes = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
    const langRoutes = (routes || []).map(route => {
      const { fallbackPath, ...rest } = route;
      return {
        ...rest,
        path: `${lang === 'en' ? '' : `/${lang}`}${getTranslatedPath(route.name, fallbackPath, lang)}`,
      };
    });
    return acc.concat(langRoutes);
  }, []);

  return localizedRoutes;
};

export default routeConfiguration;
